// Sample Logbook articles — swap with real content (or move to CMS) later.
// Each category has its own accent emoji-free icon name; the page maps the
// string to a lucide icon import.

export const CATEGORIES = {
  finance: {
    key: "finance",
    name: "Finance",
    tagline: "Cash flow, taxes, and where your money actually goes.",
    description:
      "From IFTA deadlines to per-mile math — the financial fluency every trucking operator deserves.",
    icon: "Wallet",
  },
  business: {
    key: "business",
    name: "Business",
    tagline: "Building, scaling, and structuring your operation.",
    description:
      "Authority decisions, entity structure, and the steps that turn a truck into a real business.",
    icon: "Briefcase",
  },
  operations: {
    key: "operations",
    name: "Operations",
    tagline: "The day-to-day systems that keep wheels turning.",
    description:
      "Receipts, recordkeeping, fuel strategy, and the back-office habits that compound over time.",
    icon: "Wrench",
  },
};

export const POSTS = [
  // FINANCE
  {
    slug: "ifta-filing-mistakes-owner-operators-make",
    category: "finance",
    title: "The 7 IFTA Filing Mistakes Owner-Operators Keep Making",
    excerpt:
      "Late penalties, missed jurisdictions, fuel receipts in the wrong column — the small stuff that quietly adds up at year-end.",
    readMinutes: 7,
    date: "2025-11-12",
    author: "The Linehaul Books Team",
    featured: true,
    body: [
      "Most owner-operators we work with come to us after their second or third IFTA quarter — usually because something didn't add up.",
      "Here are the seven mistakes we see most often, and how to prevent each one before the next quarter closes.",
      "1) Mileage records that don't survive an audit. The IFTA itself doesn't care how you track miles, but if you ever get audited, you need source records — not the summary you typed into a spreadsheet last Tuesday.",
      "2) Treating fuel taxes as a write-off twice. Fuel tax credits from IFTA reconciliation can quietly get double-counted on the P&L. We see this in roughly 1 in 3 sets of books we clean up.",
      "3) Missing a jurisdiction. If you drove through it, it goes on the return — even five miles in a corner of Indiana on your way through Chicago.",
      "There are four more. We'll keep this list short — the point is that none of these are dramatic. They're small, quiet errors that compound over quarters.",
    ],
  },
  {
    slug: "1099-deadlines-trucking-2026",
    category: "finance",
    title: "1099 Deadlines for Trucking Operators (and the One Most People Miss)",
    excerpt:
      "January 31 is the headline, but the contractor onboarding work starts in October. Here's the cadence.",
    readMinutes: 5,
    date: "2025-10-28",
    author: "The Linehaul Books Team",
    body: [
      "If you pay a contractor more than $600 in a calendar year and they aren't incorporated, you owe them a 1099-NEC by January 31.",
      "But the real work doesn't happen in January — it happens in October, when you should already be confirming W-9s, addresses, and payment totals.",
      "The deadline most people miss isn't filing — it's collecting accurate W-9s before you cut the first check. A missing TIN at year-end becomes a $290 penalty per form, plus backup withholding paperwork.",
    ],
  },
  {
    slug: "per-mile-profitability-math",
    category: "finance",
    title: "Per-Mile Profitability: The One Number That Tells the Truth",
    excerpt:
      "Your loaded revenue per mile, minus your true cost per mile, minus dispatch, equals your real hourly rate. Most operators have never calculated it.",
    readMinutes: 8,
    date: "2025-10-15",
    author: "The Linehaul Books Team",
    body: [
      "Revenue per mile gets all the attention. Cost per mile gets very little. The gap between them — and what's left after dispatch, factoring, and admin — is the only number that tells you whether you're running a profitable business or a treadmill.",
      "We build a per-mile P&L for every Plus and Premium client at the end of each quarter. Here's how to do a rough version on your own in about 20 minutes.",
    ],
  },

  // BUSINESS
  {
    slug: "incorporate-vs-sole-prop-trucking",
    category: "business",
    title: "Incorporate or Stay Sole Prop? A Trucker's Decision Tree",
    excerpt:
      "When the LLC actually saves you money, when it doesn't, and the trap of incorporating too early.",
    readMinutes: 6,
    date: "2025-11-05",
    author: "The Linehaul Books Team",
    featured: true,
    body: [
      "There's a viral version of this advice that says 'always incorporate, always be an S-corp.' It's wrong about half the time for solo owner-operators.",
      "The honest answer depends on three numbers: your net profit, your average miles, and your tolerance for compliance overhead.",
    ],
  },
  {
    slug: "getting-your-own-authority",
    category: "business",
    title: "Getting Your Own Authority: What the YouTube Videos Skip",
    excerpt:
      "The MC number is the easy part. The hard part is being underwritten by a factor without giving away your margin.",
    readMinutes: 9,
    date: "2025-09-30",
    author: "The Linehaul Books Team",
    body: [
      "Authority — your own MC number — is the inflection point where a driver becomes a business owner. It's also where most operators discover they're under-capitalized.",
      "We won't rehash the FMCSA paperwork. We'll tell you what your books need to look like before, during, and after you make the jump.",
    ],
  },
  {
    slug: "scaling-from-one-truck-to-three",
    category: "business",
    title: "Scaling from One Truck to Three: The Bookkeeping Inflection Point",
    excerpt:
      "Two trucks is hard. Three trucks is a real business. The accounting changes more between truck two and three than anywhere else in your growth.",
    readMinutes: 7,
    date: "2025-09-18",
    author: "The Linehaul Books Team",
    body: [
      "When you go from one truck to two, you can still hold most of the operation in your head. By truck three, the spreadsheets break.",
      "Per-truck P&Ls, dispatch reconciliations, contractor payments, and IFTA across multiple units stop being a part-time task. Either you're paying someone, or you're losing miles you should've been driving.",
    ],
  },

  // OPERATIONS
  {
    slug: "receipt-digitization-system",
    category: "operations",
    title: "A Receipt Digitization System That Actually Survives a Year on the Road",
    excerpt:
      "Cab-friendly capture, weekly review, and the 30-second habit that saves you 8 hours at tax time.",
    readMinutes: 4,
    date: "2025-11-20",
    author: "The Linehaul Books Team",
    body: [
      "Receipt envelopes don't work. Shoeboxes don't work. Photos on your phone almost work — until you can't find the one from the truck stop in Wyoming six months ago.",
      "Here's the system we set up with every Plus and Premium client during onboarding.",
    ],
  },
  {
    slug: "fuel-cards-vs-cash-advance",
    category: "operations",
    title: "Fuel Cards vs Cash Advances: The Real Math",
    excerpt:
      "Discounts look great in the brochure. The hidden costs are in the broker advance fees and the float.",
    readMinutes: 6,
    date: "2025-10-08",
    author: "The Linehaul Books Team",
    body: [
      "Every fuel card pitch leads with the discount per gallon. None of them lead with the float — the gap between when you swipe and when your account settles.",
      "Cash advances from brokers carry a fee that looks small as a percentage and enormous as an annualized rate. We'll walk through the math.",
    ],
  },
  {
    slug: "audit-ready-recordkeeping",
    category: "operations",
    title: "Audit-Ready Recordkeeping for Operators Who Hate Paperwork",
    excerpt:
      "What to keep, how long to keep it, and the cloud setup that means you never touch a file cabinet.",
    readMinutes: 5,
    date: "2025-09-22",
    author: "The Linehaul Books Team",
    body: [
      "If you ever get audited — by the IRS, by the state, or for IFTA — you'll have about 30 days to produce records you should have been keeping all along.",
      "Here's the minimum viable filing system that takes 10 minutes a week and survives an audit.",
    ],
  },
];

export const getPostsByCategory = (cat) =>
  POSTS.filter((p) => p.category === cat);

export const getFeaturedPosts = () => POSTS.filter((p) => p.featured);

export const getPostBySlug = (slug) =>
  POSTS.find((p) => p.slug === slug) || null;

export const getRecentPosts = (limit = 6) =>
  [...POSTS]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, limit);

export const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
